/* eslint-disable no-restricted-globals */
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';

import * as apiItemUom from '../../services/item/api_item_uom';
import * as apiCommon from '../../services/apiCommon';
import * as sagaCommon from '../../sagas/sagasCommon';
import {
  SET_ITEM_UOM_LIST,
  GET_ITEM_UOM_LIST_IN_PROGRESS,
  GET_ITEM_UOMS,
  SAVE_ITEM_UOM,
  DELETE_ITEM_UOM,
  GET_ITEM_UOM,
  SET_MODAL_LOADING,
} from '../../constants/constant_action_types';
import {
  setItemUom,
  setItemUomPagination,
  closeItemUomModal,
  clearItemUom,
} from '../../actions/actionCreatorItemUom';
import { getItemDetails } from '../../components/admin/item/modals/item_location/ItemLocationSelector';
import { getItemBarcodes, getItemQrcodes } from '../../components/admin/item/tabs/ItemSelector';
import { keyBy } from 'lodash';
import { change } from 'redux-form';
import { ItemAddForm } from '../../constants/constant_form_names';

export default function* (app) {
  try {
    yield all([
      fork(saveItemUomSaga, app),
      fork(deleteItemUomSaga, app),
      fork(getItemUomSaga, app),
      fork(getItemUomsSaga, app),
    ]);
  } catch (e) {
    console.error('@saga_item_uom: ', e.message || e.stack);
    throw e;
  }
}

function* saveItemUomSaga(feathersApp) {
  yield takeLatest(SAVE_ITEM_UOM, callSaveItemUom, feathersApp);
}

function* callSaveItemUom(feathersApp, action) {
  try {
    yield sagaCommon.showProgressSaveRecord();
    const { tenant, user } = yield select();
    const { data } = action;
    const params = { tenant, user, data };
    const itemId = data.item_id;

    const savedItemUom = yield call(apiItemUom.saveItemUom, feathersApp, params);
    yield call(updateItemUPC, savedItemUom);
    yield put({ type: GET_ITEM_UOMS, filters: { item: itemId } });
    yield put(setItemUom(data));
    yield sagaCommon.doneSaveRecord();

    yield put(closeItemUomModal());
    yield put(clearItemUom());
  } catch (e) {
    console.error(e.stack);
    yield sagaCommon.showErrorSaveRecord(e);
  }
}

function* updateItemUPC(itemUom) {
  const itemBarcodes = yield select(getItemBarcodes); 
  const strBarcodes = itemUom.uom_barcodes.map(({ barcode }) => barcode);
  const fItemBarcodes = itemBarcodes.filter((barcode) => {
    return !(barcode.item_uom_id === itemUom.id && strBarcodes.indexOf(barcode.barcode) === -1)
  });

  const idxBarcode = keyBy(fItemBarcodes, 'barcode');
  itemUom.uom_barcodes.forEach((uomBarcode) => {
    const { barcode } = uomBarcode;
    idxBarcode[barcode] = uomBarcode;
  });
  yield put(change(ItemAddForm, 'barcodes', Object.values(idxBarcode)));

  const itemQrcodes = yield select(getItemQrcodes);
  const strQrcodes = itemUom.uom_qrcodes.map(({ qrcode }) => qrcode);
  const fItemQrcodes = itemQrcodes.filter((qrcode) => 
    !(qrcode.item_uom_id === itemUom.id && strQrcodes.indexOf(qrcode.qrcode) === -1)
  );

  const idxQrcode = keyBy(fItemQrcodes, 'qrcode');
  itemUom.uom_qrcodes.forEach((uomQrcode) => {
    const { qrcode } = uomQrcode;
    idxQrcode[qrcode] = uomQrcode;
  });

  yield put(change(ItemAddForm, 'qrcodes', Object.values(idxQrcode)));
}

function* deleteItemUomSaga(feathersApp) {
  yield takeLatest(DELETE_ITEM_UOM, callDeleteItemUom, feathersApp);
}

function* callDeleteItemUom(feathersApp, action) {
  try {
    const ok = confirm('Are you sure you want to delete this record?');
    if (!ok) return;

    yield sagaCommon.showProgressDeleteRecord();
    const { tenant, user } = yield select();
    const { data } = action;
    const params = { tenant, user, data };
    const itemId = data.item_id;
    yield call(apiItemUom.deleteItemUom, feathersApp, params);
    yield sagaCommon.doneDeleteRecord();
    yield put({ type: GET_ITEM_UOMS, filters: { item: itemId } });
    yield put(closeItemUomModal());
    yield put(clearItemUom());
  } catch (e) {
    yield sagaCommon.showErrorDeleteRecord(e);
  }
}

function* getItemUomSaga(feathersApp) {
  yield takeLatest(GET_ITEM_UOM, callGetItemUom, feathersApp);
}

function* callGetItemUom(feathersApp, action) {
  try {
    const { id } = action;

    yield put({ type: SET_MODAL_LOADING, loading: true });
    const { user } = yield select();

    if (id) {
      const itemUom = yield call(apiItemUom.getItemUom, feathersApp, {
        user,
        id,
      });
      yield put(setItemUom(itemUom));
    } else {
      const { item_id } = yield select(getItemDetails);
      const itemUom = {
        item_id,
        active: true,
        preferred: false,
        uom_barcodes: [],
        uom_qrcodes: [],
      };
      
      yield put(setItemUom(itemUom));
    }
    yield put({ type: SET_MODAL_LOADING, loading: false });
  } catch (e) {
    yield sagaCommon.showErrorDataById(e);
  }
}

function* getItemUomsSaga(feathersApp) {
  yield takeLatest(GET_ITEM_UOMS, callGetItemUoms, feathersApp);
}

function* callGetItemUoms(feathersApp, action) {
  try {
    const { filters = {} } = action;
    const { item } = filters;
    const { requested_page, rows_per_page } = filters;
    if (!item) {
      yield put(setItemUomPagination(1, 10, 0));
      yield put({ type: SET_ITEM_UOM_LIST, list: [] });
      return;
    }

    yield put({ type: GET_ITEM_UOM_LIST_IN_PROGRESS });
    const dataParams = {
      filters: { ...filters, location_type: 'store' },
      list_type: 'item_uom',
    };

    const storeItemUomResult = yield call(
      apiCommon.getDocs,
      feathersApp,
      dataParams
    );
    const storeItemUoms = storeItemUomResult.data;

    if (storeItemUomResult.total) {
      yield put(
        setItemUomPagination(
          requested_page,
          rows_per_page,
          storeItemUomResult.total
        )
      );
    } else {
      yield put(setItemUomPagination(1, 10, 0));
    }
    yield put({ type: SET_ITEM_UOM_LIST, list: storeItemUoms });
  } catch (e) {
    yield sagaCommon.showErrorListTable(e);
  }
}
